import { useState } from 'react';

import Button from 'components/helpers/Button/Button';
import Icon from 'components/helpers/Icon';
import styles from './Header.module.scss';
import classNames from 'classnames/bind';

import { SearchIconProps } from './HeaderTypes';
import { useDictionary } from 'lib/utils';

const getDefaultSearchText = (searchLinkParam: string | undefined, termToken = '{term}') => {
  if (!searchLinkParam) {
    return '';
  }

  let $defaultSearchText = '';
  const $currentUrl = document.location.href;
  const $searchParam = searchLinkParam.replace(termToken, '');
  const $index = $currentUrl.indexOf($searchParam);

  if ($index > 0) {
    $defaultSearchText = $currentUrl.substring($index);
    $defaultSearchText = $defaultSearchText.replace($searchParam, '');
  }

  return decodeURIComponent($defaultSearchText);
};

const SearchBox = (props: SearchIconProps): React.JSX.Element | null => {
  const cx = classNames.bind(styles);
  const $termToken = '{term}';
  const $divId = 'search-box';

  const $searchButtonAriaLabel = useDictionary('Aria-Search');
  const $searchBoxPlaceholder = useDictionary('Search-Box-Placeholder', 'what are you looking for?');
  const $closeLabel = useDictionary('Close');

  const { isMobile, displayMenu, displayType, onClose, searchPageUrl, searchLinkParam } = props;

  const $displayType = displayType || 'desktop';
  const $displayTypeClassName = `display-type-${$displayType}`;

  const $defaultSearchTerm = getDefaultSearchText(searchLinkParam, $termToken);
  const [searchTerm, setSearchTerm] = useState($defaultSearchTerm);

  const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const redirectToSearch = () => {
    if (!searchTerm.trim() || !searchPageUrl) {
      return;
    }

    const $searchUrl = `${searchPageUrl}${searchLinkParam}`;
    document.location.href = $searchUrl.replace($termToken, encodeURIComponent(searchTerm));
  };

  return (
    <>
      {isMobile && displayMenu ? <div className={cx('header-overlay')}></div> : null}

      <div
        className={cx(
          'search-box',
          'flyout',
          $displayTypeClassName,
          { 'mobile-view': isMobile },
          { 'desktop-view': !isMobile }
        )}
        id={$divId}
        aria-label={$searchButtonAriaLabel}
        aria-hidden={!displayMenu}
        hidden={!displayMenu}
      >
        <div className={cx('mobile-only', 'close-header')}>
          <div className={cx('horizontal-line')} onClick={onClose} />
          <button className={cx('closeIcon')} onClick={onClose} title={$closeLabel}>
            <Icon variant="close" />
          </button>
        </div>

        <div className={cx('search-box-container')}>
          <input
            type="text"
            className={cx('search-box-input')}
            placeholder={$searchBoxPlaceholder}
            onChange={(e) => onSearchInputChange(e)}
            onKeyUp={(e) => e.key === 'Enter' && redirectToSearch()}
            value={searchTerm}
          />

          <Button
            type="button"
            aria-label={$searchButtonAriaLabel}
            title={$searchButtonAriaLabel}
            onClick={redirectToSearch}
            disabled={!searchTerm.trim()}
          >
            <Icon variant="search16" className={cx('search-button-icon')} />
          </Button>
        </div>
      </div>
    </>
  );
};

export default SearchBox;
